/**
 * ``useProducerLatestStatus`` — TanStack Query hook around
 * ``GET /api/query/producer/{id}/latest-status`` (issue #257; enriched
 * by the ADR-032 §4.2 PR4 4d slice).
 *
 * Feeds the Entity Details panel's STATUS + POSITION sections. The
 * endpoint is **producer-scoped** and **unbounded in time**, unlike
 * the viewport-scoped Map snapshot — so the panel renders live status
 * even when the selected producer isn't in the current Map viewport
 * (e.g., a Platforms-list selection of a producer off-screen).
 *
 * # Response shape (PR4)
 *
 * The BFF now returns the :type:`ProducerLatestStatusResponse` wrapper
 * — ``{ status: StatusItem, geometry: StatusGeometry | null }`` — per
 * REQ-QUERY-026 / REQ-QUERY-027, **not** a bare ``StatusItem``. The
 * geometry rides the wrapper (never ``StatusItem``) so it stays off the
 * bulk snapshot list, which deliberately never deserializes the status
 * payload blob (ADR-032 §4.2; REQ-QUERY-007 perf budget). This hook
 * therefore yields the whole wrapper so the STATUS section can render
 * the enriched scalars (``mode_label`` / power / ``active_task_id``) and
 * the field-of-view summary together.
 *
 * A ``404`` means the producer has no status messages yet; that
 * resolves to ``null`` (an empty state the sections render), **not**
 * an error.
 *
 * Cross-references:
 *   - REQ-UI-013 / REQ-UI-049 / REQ-UI-065 (STATUS + POSITION + the
 *     PR4 status enrichment).
 *   - REQ-QUERY-026 (enriched status scalars) / REQ-QUERY-027 (geometry).
 *   - Flow Q §"Frame 2".
 *   - ADR-024 §4 / ADR-032 §4.2.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

export type StatusItem = components["schemas"]["StatusItem"];
export type StatusGeometry = components["schemas"]["StatusGeometry"];
export type ProducerLatestStatusResponse = components["schemas"]["ProducerLatestStatusResponse"];

/** Same read-role gate as the Map snapshot (REQ-AUTH-024..027). */
export const PRODUCER_LATEST_STATUS_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/**
 * Refetch interval. 30 s — producers report status at ~1 Hz but the
 * BFF rate-limits to 1/s and the Entity Details panel is a glance
 * surface, not the live Map; a half-minute cadence keeps STATUS /
 * POSITION fresh without thrashing the BFF.
 */
export const PRODUCER_LATEST_STATUS_REFETCH_MS = 30_000;

export function producerLatestStatusQueryKey(producerId: string): readonly unknown[] {
  return ["producer-latest-status", producerId] as const;
}

/**
 * Fetch + validate a producer's latest status, the shared query function
 * behind both this hook's :func:`useQuery` and the Map's
 * :func:`usePlatformFocusFlyTo` ``fetchQuery`` (REQ-UI-096). Sharing one
 * function + the :func:`producerLatestStatusQueryKey` means the "Select on
 * Map" fetch dedupes against / serves from the Entity Details panel's cache
 * for the same producer rather than re-hitting the BFF.
 *
 * Resolves ``null`` on a ``404`` (the producer has no status messages yet —
 * an empty state, not an error); rejects with :class:`ApiError` on any other
 * failure or a response that fails :func:`isLatestStatusResponse`.
 *
 * The producer-scoped, time-unbounded endpoint is the only client-side
 * position source for an **off-viewport** platform — the Map snapshot is
 * viewport-scoped and the Platforms list carries no coordinates — so it is
 * what lets "Select on Map" recenter to a platform that isn't on screen.
 *
 * Cross-references:
 *   - REQ-UI-096 (cross-pane "Select on Map") / REQ-QUERY-026 / REQ-QUERY-027.
 *   - ADR-032 §4.2; map/use-platform-focus-fly-to.ts (the second consumer).
 */
export async function fetchProducerLatestStatus(
  producerId: string,
  signal?: AbortSignal,
): Promise<ProducerLatestStatusResponse | null> {
  const url = `/api/query/producer/${encodeURIComponent(producerId)}/latest-status`;
  try {
    const raw = await apiFetch<unknown>(url, { signal });
    if (!isLatestStatusResponse(raw)) {
      console.error(
        "[meridian/entity-details/use-producer-latest-status] response failed shape validation",
        raw,
      );
      throw new ApiError(500, raw, "latest-status response did not match the expected shape");
    }
    return raw;
  } catch (err) {
    // 404 = producer has no status messages yet → empty state, not error.
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

export interface UseProducerLatestStatusOptions {
  producerId: string | null;
}

export function useProducerLatestStatus(
  options: UseProducerLatestStatusOptions,
): UseQueryResult<ProducerLatestStatusResponse | null, ApiError> {
  const session = useSession();
  const hasRole = session.hasAnyRole(PRODUCER_LATEST_STATUS_READER_ROLES);
  const enabled = options.producerId !== null && hasRole;

  return useQuery<ProducerLatestStatusResponse | null, ApiError>({
    queryKey:
      options.producerId !== null
        ? producerLatestStatusQueryKey(options.producerId)
        : ["producer-latest-status", "disabled"],
    queryFn: async ({ signal }) => {
      if (options.producerId === null) {
        throw new ApiError(0, null, "useProducerLatestStatus: query ran without producerId");
      }
      // Delegate to the shared fetch+guard so the Map's "Select on Map"
      // ``fetchQuery`` (same queryFn + key) dedupes against this cache.
      return fetchProducerLatestStatus(options.producerId, signal);
    },
    enabled,
    refetchInterval: enabled ? PRODUCER_LATEST_STATUS_REFETCH_MS : false,
    retry: (failureCount, error) => {
      if (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403 || error.status === 404)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Wire-shape guard for the :type:`ProducerLatestStatusResponse` wrapper.
 *
 * Validates the ``status`` member against :func:`isStatusItem`; ``geometry``
 * is optional/nullable (a platform that reported no drawable rings) so it is
 * only shape-checked when present and non-null.
 */
function isLatestStatusResponse(value: unknown): value is ProducerLatestStatusResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  if (!isStatusItem(obj.status)) return false;
  // ``geometry`` is optional + nullable; reject only a present non-object.
  if (obj.geometry !== undefined && obj.geometry !== null && typeof obj.geometry !== "object") {
    return false;
  }
  return true;
}

/**
 * Wire-shape guard for a single :type:`StatusItem`.
 *
 * ``latitude_deg`` / ``longitude_deg`` / ``elevation_m`` are **nullable** here
 * (``number | null``) — an externally-provided sensor can report status without
 * a node location, and this producer-scoped latest-status read deliberately
 * preserves that location-less status instead of dropping it — the STATUS
 * panel must populate Health/Mode/Power even when coordinates are absent
 * (REQ-UI-065 / operator-visibility, CLAUDE.md §8.1). So the guard
 * accepts ``null`` OR ``number`` for the three position scalars, mirroring the
 * nullable-position precedent set by detections/alerts (REQ-INGEST-044;
 * ``detections-panel.tsx`` / ``alert-sections.tsx``).
 *
 * This is the **only** position guard that relaxes: the Map snapshot / trail
 * path keeps :func:`isSnapshotItem` strict (a snapshot row always carries a
 * position — a ``null`` there is a server bug, not a valid state), because the
 * live SSE/map feed still drops location-less status (an unplaceable platform
 * has no marker).
 */
function isStatusItem(value: unknown): value is StatusItem {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  if (typeof obj.producer_id !== "string") return false;
  if (typeof obj.time !== "string") return false;
  if (typeof obj.health_state !== "number") return false;
  if (typeof obj.mode !== "number") return false;
  // Nullable position scalars — accept null OR a number; reject any other type.
  if (obj.latitude_deg !== null && typeof obj.latitude_deg !== "number") return false;
  if (obj.longitude_deg !== null && typeof obj.longitude_deg !== "number") return false;
  if (obj.elevation_m !== null && typeof obj.elevation_m !== "number") return false;
  return true;
}
